import type { ReactNode } from 'react';

import { ApiError } from '@ottd/api';

import { Skeleton } from '@/components/ui/skeleton';
import { tr } from '@/lib/locale';
import { cn } from '@/lib/utils';

/*
 * Standard async states for every list/detail screen (phase-7a: loading /
 * empty / error on every list). Announces transitions via aria-live and keeps
 * the skeleton out of the a11y tree (aria-busy).
 */

interface QueryStateProps {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  isEmpty?: boolean;
  /** Overrides the generic "Nothing here yet." for this screen. */
  emptyLabel?: string;
  /** Skeleton shown while loading. */
  skeleton?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * The message an error renders as. An `ApiError`'s own message comes from the
 * backend's problem document, which is already in the request's language; only
 * the two statuses we word ourselves and the generic fallback are translated
 * here.
 */
function messageFor(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 404) return tr('common:state.notFound');
    if (error.status === 403) return tr('common:state.noAccess');
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return tr('common:state.error');
}

export function QueryState({
  isLoading,
  isError,
  error,
  isEmpty = false,
  emptyLabel,
  skeleton,
  children,
  className,
}: QueryStateProps) {
  return (
    <div className={className} aria-live="polite" aria-busy={isLoading}>
      {isLoading ? (skeleton ?? <DefaultSkeleton />) : null}
      {!isLoading && isError ? (
        <p className="text-destructive font-mono text-sm" role="alert">
          {messageFor(error)}
        </p>
      ) : null}
      {!isLoading && !isError && isEmpty ? (
        <p className="text-dim font-mono text-sm">{emptyLabel ?? tr('common:state.empty')}</p>
      ) : null}
      {!isLoading && !isError && !isEmpty ? children : null}
    </div>
  );
}

export function DefaultSkeleton({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}
